import React from 'react';
import PropTypes from 'prop-types';
import { Provider, connect } from 'react-redux';
import { Router, Route } from 'react-router-dom';
import { LoadSpinner } from 'cstar-react-primitives/lib/redux-form/LoadSpinner';
import { Helmet } from 'react-helmet';
import { injectIntl, intlShape } from 'react-intl';

import { getIntlLocale, getIntlCurrencyCode } from '../intl/intlSelectors';
import Header from '../header/Header';
import Footer from '../footer/Footer';
import PromoBanner from '../promoDisplay/PromoBanner';
import NotificationPortal from '../notifications/NotificationPortal';
import ScrollToTopWrap from '../primitive/ScrollToTopWrap';
import { getDisplayBannerPaths } from '../routing/flowSelectors';
import { flowFactory } from '../routing/FlowRoute';
import ProgramIntlProvider from '../program/ProgramIntlProvider';
import { ConnectedCookieConsentBanner } from '../consent/CookieConsentBanner';
import { ConnectedTestEnvironmentBanner } from '../testEnvironment/TestEnvironmentBanner';
import { getBrandName } from '../brand/brandSelectors';
import headerMessages from '../header/headerMessages';

import { getEnv } from './bootstrap';
import appStore from './appStore';
import { getShowAppLoadingSpinner } from './appSelectors';


const Flow = flowFactory(appStore.getState());


export const App = (props) => {
    const {
        intl,
        loading,
        locale,
        currency,
        displayBannerPaths,
        brandName
    } = props;
    const showTestEnv = getEnv() !== 'PROD';
    return (
        <div className="app" key={`${locale}-${currency}`}>
            <Helmet>
                {}
                <html lang={locale} />
                <title>{intl.formatMessage(headerMessages.pageTitle, { brandName })}</title>
            </Helmet>
            <ConnectedCookieConsentBanner />
            {}

            { showTestEnv && (
                <ConnectedTestEnvironmentBanner />
            )}

            <Header {...props} />

            {}
            <Route path={displayBannerPaths} component={PromoBanner} />

            {}
            <main className="app-wrapper">
                {loading && <LoadSpinner overlay />}
                <Flow {...props} />
            </main>

            {}
            <Footer {...props} />

            <NotificationPortal {...props} />
        </div>
    );
};

App.defaultProps = {
    loading: false,
    currency: null
};

App.propTypes = {
    loading: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    currency: PropTypes.string,
    displayBannerPaths: PropTypes.array.isRequired,
    brandName: PropTypes.string.isRequired,
    intl: intlShape.isRequired
};

const mapStateToProps = state => ({
    loading: getShowAppLoadingSpinner(state),
    locale: getIntlLocale(state), 
    currency: getIntlCurrencyCode(state), 
    displayBannerPaths: getDisplayBannerPaths(state),
    brandName: getBrandName(state)
});

export const ConnectedApp = connect(mapStateToProps)(injectIntl(App));


const ReduxApp = ({ history }) => (
    <Provider store={appStore}>
        <ProgramIntlProvider>
            <Router history={history}>
                {}
                <ScrollToTopWrap>
                    <Route component={ConnectedApp} />
                </ScrollToTopWrap>
            </Router>
        </ProgramIntlProvider>
    </Provider>
);

ReduxApp.propTypes = {
    history: PropTypes.object.isRequired
};

export default ReduxApp;
